// TanStack Router configuration
// Defines the full route tree using the code API (no file-based routing).
// Uses createBrowserHistory with a Netlify catch-all redirect (public/_redirects)
// so deep links and refreshes work, while leaving the URL hash free for
// Supabase auth callbacks (e.g. #access_token=...).

import {
  createBrowserHistory,
  createRootRoute,
  createRoute,
  createRouter,
  HeadContent,
  Outlet,
} from "@tanstack/react-router";
import { createPortal } from "react-dom";
import { ToastBar, Toaster } from "react-hot-toast";
import AuthGuard from "./components/AuthGuard";
import Dashboard from "./components/Dashboard";
import PaymentSuccess from "./components/PaymentSuccess";
import PrivacyPolicy from "./components/PrivacyPolicy";
import ResetPassword from "./components/ResetPassword";
import { ThemeProvider } from "./contexts/ThemeContext";

// ─── Root route ──────────────────────────────────────────────────────────────
const rootRoute = createRootRoute({
  component: () => (
    <ThemeProvider>
      <HeadContent />
      <Outlet />
      {createPortal(
        <Toaster
          position="top-center"
          containerStyle={{ top: "calc(env(safe-area-inset-top) + 0.75rem)" }}
          toastOptions={{
            duration: 2000,
            success: {
              style: {
                background: "var(--toast-success-bg)",
                color: "var(--toast-success-color)",
              },
              iconTheme: {
                primary: "var(--toast-success-color)",
                secondary: "var(--toast-success-bg)",
              },
            },
            blank: {
              style: {
                background: "var(--toast-info-bg)",
                color: "var(--toast-info-color)",
              },
              iconTheme: {
                primary: "var(--toast-info-color)",
                secondary: "var(--toast-info-bg)",
              },
            },
            error: {
              style: {
                background: "var(--toast-error-bg)",
                color: "var(--toast-error-color)",
              },
              iconTheme: {
                primary: "var(--toast-error-color)",
                secondary: "var(--toast-error-bg)",
              },
            },
          }}
        >
          {(t) => (
            <ToastBar
              toast={t}
              style={{
                ...t.style,
                animation: t.visible
                  ? "none"
                  : "toast-slide-up 100ms ease-in forwards",
                transform: t.visible ? "translateY(0)" : "translateY(-100%)",
                opacity: t.visible ? 1 : 0,
              }}
            />
          )}
        </Toaster>,
        document.body,
      )}
    </ThemeProvider>
  ),
});

// ─── /reset-password ─────────────────────────────────────────────────────────
const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reset-password",
  head: () => ({ meta: [{ title: "Pantry - Reset Password" }] }),
  component: () => <ResetPassword />,
});

// ─── /payment-success ────────────────────────────────────────────────────────
const paymentSuccessRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/payment-success",
  head: () => ({ meta: [{ title: "Pantry - Payment" }] }),
  component: () => <PaymentSuccess />,
});

// ─── /privacy ────────────────────────────────────────────────────────────────
const privacyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/privacy",
  head: () => ({ meta: [{ title: "Pantry - Privacy Policy" }] }),
  component: () => <PrivacyPolicy />,
});

// ─── Dashboard layout route ───────────────────────────────────────────────────
// Renders AuthGuard + Dashboard once and keeps it mounted across all tab
// navigations. Child routes are empty — they only contribute the URL and title.
const dashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "dashboard",
  component: () => (
    <AuthGuard>
      <Dashboard />
    </AuthGuard>
  ),
});

// ─── / (index) ──────────────────────────────────────────────────────────────
// No redirect — AuthGuard handles unauthenticated users by showing LandingPage
// at the clean URL. Authenticated users see Dashboard with the inventory tab
// active (activeTab falls back to "inventory" when the path is "/").
const indexRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: "/",
  head: () => ({ meta: [{ title: "Pantry - Kitchen Inventory App" }] }),
});

// ─── /inventory ──────────────────────────────────────────────────────────────
const inventoryRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: "/inventory",
  head: () => ({ meta: [{ title: "Pantry - Inventory" }] }),
});

// ─── /shopping ───────────────────────────────────────────────────────────────
const shoppingRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: "/shopping",
  head: () => ({ meta: [{ title: "Pantry - Shopping List" }] }),
});

// ─── /recipes ────────────────────────────────────────────────────────────────
const recipesRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: "/recipes",
  head: () => ({ meta: [{ title: "Pantry - Recipes" }] }),
});

// ─── /purchase ───────────────────────────────────────────────────────────────
// Empty child of dashboardRoute — Dashboard stays mounted and renders
// LicenseRequired in its main content area when it detects no license.
const purchaseRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: "/purchase",
  head: () => ({ meta: [{ title: "Pantry - Purchase License" }] }),
});

// ─── Router ──────────────────────────────────────────────────────────────────
const routeTree = rootRoute.addChildren([
  resetPasswordRoute,
  paymentSuccessRoute,
  privacyRoute,
  dashboardRoute.addChildren([
    indexRoute,
    inventoryRoute,
    shoppingRoute,
    recipesRoute,
    purchaseRoute,
  ]),
]);

const browserHistory = createBrowserHistory();

export const router = createRouter({
  routeTree,
  history: browserHistory,
});

// Type-safe router registration
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
